/**
 * A/B testing allocation utility.
 * Assigns users to experiment variants by configurable percentages, with consistent
 * assignment per user (persisted in localStorage). Use with Amplitude for tracking.
 */

const STORAGE_PREFIX = "woba_ab_";
const ANONYMOUS_ID_KEY = `${STORAGE_PREFIX}anonymous_id`;

export type ExperimentVariant = {
  /** Variant key (e.g. "A", "B", "control") */
  name: string;
  /** Percentage 0–100 (will be normalized if sum !== 100) */
  percentage: number;
};

export type GetExperimentVariantResult = {
  /** Selected variant name for rendering */
  variant: string;
  /** Experiment key for Amplitude tracking */
  experimentKey: string;
};

export type AbAssignmentReportCallback = (data: {
  experimentKey: string;
  variant: string;
  userId?: string;
  leagueId?: number;
  companyId?: number;
}) => void;

/** Normalized variant with percentage summing to 100 */
type NormalizedVariant = { name: string; percentage: number };

/**
 * Normalizes variant percentages to sum to 100.
 * If sum is 0 or invalid, returns first variant at 100%.
 */
function normalizePercentages(
  variants: ExperimentVariant[],
): NormalizedVariant[] {
  if (!variants?.length) return [];
  const sum = variants.reduce((s, v) => s + (v.percentage ?? 0), 0);
  if (sum <= 0) {
    return [{ name: variants[0].name, percentage: 100 }];
  }
  if (sum === 100) {
    return variants.map((v) => ({ name: v.name, percentage: v.percentage }));
  }
  return variants.map((v) => ({
    name: v.name,
    percentage: Math.round((100 * (v.percentage ?? 0)) / sum),
  }));
}

/**
 * Deterministic string hash to [0, 1).
 * Same input always yields same output (for consistent assignment).
 */
function hashToUnit(str: string): number {
  let h = 0;
  const s = str || "";
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h = (h << 5) - h + c;
    h = h & h;
  }
  const u = (h >>> 0) / (0xffff_ffff + 1);
  return u >= 0 && u < 1 ? u : Math.abs(u) % 1;
}

/**
 * Picks a variant by weight given a value in [0, 1).
 */
function pickVariantByWeight(
  normalized: NormalizedVariant[],
  value: number,
): string {
  if (!normalized.length) return "A";
  let acc = 0;
  const threshold = value * 100;
  for (const v of normalized) {
    acc += v.percentage;
    if (threshold < acc) return v.name;
  }
  return normalized[normalized.length - 1].name;
}

function getOrCreateAnonymousId(): string {
  try {
    const key = ANONYMOUS_ID_KEY;
    let id = localStorage.getItem(key);
    if (!id) {
      id = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return `anon_${Date.now()}`;
  }
}

export function getStoredVariant(experimentKey: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${experimentKey}`);
  } catch {
    return null;
  }
}

export function setStoredVariant(experimentKey: string, variant: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${experimentKey}`, variant);
  } catch {
    // ignore storage errors
  }
}

/** Removes cached variant (e.g. after rename or invalid value). */
export function clearStoredVariant(experimentKey: string): void {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${experimentKey}`);
  } catch {
    // ignore storage errors
  }
}

/**
 * Returns the experiment variant for the current user.
 * Persists the choice in localStorage so the user stays in the same variant on reload.
 *
 * @param experimentKey - Unique key for the experiment (e.g. "lia_chat_placement")
 * @param variants - List of variants with names and percentages (e.g. [{ name: "A", percentage: 50 }, { name: "B", percentage: 50 }])
 * @param userId - Optional stable user id (e.g. from auth). If not provided, uses a persistent anonymous id per browser.
 * @param options - Optional. onAssignmentReport (and leagueId/companyId) used when a new assignment is made so the backend can store counts and league/company per group.
 * @returns { variant, experimentKey } for rendering and Amplitude tracking. On any error, returns first variant as fallback.
 */
export function getExperimentVariant(
  experimentKey: string,
  variants: ExperimentVariant[],
  userId?: string,
  options?: {
    onAssignmentReport?: AbAssignmentReportCallback;
    leagueId?: number;
    companyId?: number;
  },
): GetExperimentVariantResult {
  const fallback: GetExperimentVariantResult = {
    variant: variants?.[0]?.name ?? "A",
    experimentKey: experimentKey || "unknown",
  };

  try {
    if (!experimentKey || !variants?.length) return fallback;

    const stored = getStoredVariant(experimentKey);
    if (stored != null) {
      const exists = variants.some((v) => v.name === stored);
      if (exists) {
        return { variant: stored, experimentKey };
      }
      // Stored variant no longer in config; re-assign below
    }

    const normalized = normalizePercentages(variants);
    const seed = userId ?? getOrCreateAnonymousId();
    const value = hashToUnit(`${experimentKey}:${seed}`);
    const variant = pickVariantByWeight(normalized, value);
    setStoredVariant(experimentKey, variant);
    options?.onAssignmentReport?.({
      experimentKey,
      variant,
      userId,
      leagueId: options?.leagueId,
      companyId: options?.companyId,
    });
    return { variant, experimentKey };
  } catch {
    return fallback;
  }
}
